import * as t from "io-ts";

/**
 * A production bucket returned as calculation evidence. Missing time buckets are synthesized by the
 * allocator and therefore do not have the persisted id required by ProductionVolumeValidator.
 */
export const WellAllocationProductionVolumeValidator = t.intersection([
  t.type({
    objectId: t.string,
    objectType: t.string,
    timeGroup: t.string,
    dttm: t.number,
    quantity: t.number,
    unitOfMeasure: t.string,
    regionId: t.string,
  }),
  t.partial({
    id: t.string,
    readingTemporalIndicator: t.string,
    generatedFromMeterReading: t.boolean,
    objectName: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    freightTypeCategory: t.string,
    category: t.keyof({WATER: null, OIL: null}),
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export type WellAllocationProductionVolume = t.TypeOf<typeof WellAllocationProductionVolumeValidator>;

export const WellAllocationValidator = t.type({
  siteId: t.string,
  siteName: t.string,
  wellId: t.string,
  wellname: t.string,
  containerId: t.string,
  containerName: t.string,
  volume: t.number,
  adjustedVolume: t.number,
  allocation: t.number,
  average: t.number,
  observedChunkCount: t.number,
  expectedChunkCount: t.number,
  temporalIndicator: t.string,
  productionVolumes: t.array(WellAllocationProductionVolumeValidator),
});

export const WellAllocationArrayValidator = t.array(WellAllocationValidator);

export type WellAllocation = t.TypeOf<typeof WellAllocationValidator>;

export const WellAllocationConfigurationValidator = t.type({
  allocationType: t.keyof({WEEKLY: null, RETROSPECTIVE: null}),
  eligibilityMethod: t.keyof({
    PER_SITE: null,
    TICKET_WELL: null,
    DISABLED: null,
    CONTAINER_WELLS: null,
  }),
  timeGroup: t.keyof({
    HOURLY: null,
    DAILY: null,
    WEEKLY: null,
    SEMI_MONTHLY: null,
    MONTHLY: null,
  }),
  chunkCount: t.number,
  weekStartsOn: t.string,
  windowStartDttm: t.number,
  /** Exclusive end of the production window. */
  windowEndDttm: t.number,
  temporalIndicator: t.string,
  timeZone: t.string,
  category: t.keyof({WATER: null, OIL: null}),
});

export type WellAllocationConfiguration = t.TypeOf<typeof WellAllocationConfigurationValidator>;

export const WellAllocationDetailsValidator = t.type({
  containerId: t.string,
  containerName: t.string,
  ticketFromDttm: t.number,
  ticketToDttm: t.number,
  configuration: WellAllocationConfigurationValidator,
  allocations: WellAllocationArrayValidator,
});

export type WellAllocationDetails = t.TypeOf<typeof WellAllocationDetailsValidator>;

export const WellAllocationRequestValidator = t.intersection([
  t.type({
    fromDttm: t.number,
    toDttm: t.number,
    containerId: t.string,
    category: t.string,
    regionId: t.string,
  }),
  t.partial({
    serviceTypeId: t.string,
    wellId: t.string,
    database: t.string,
  }),
]);

export type WellAllocationRequest = t.TypeOf<typeof WellAllocationRequestValidator>;
